import { useEffect, useState } from "react";
import { Check, Sparkles, Loader2, Layers } from "lucide-react";
import { toast } from "react-toastify";
import {
  getPlans,
  getUserSubscription,
  getCreditPlans,
  getAddonPlans,
  createAddonOrder,
  verifyAddonPayment,
} from "../apis/subscription";
import { loadScript, openRazorpayCheckout } from "../utils/razorpay";
import { startPlatformCheckout, isAutopayOn } from "../utils/platformCheckout";
import { resolveCheckoutBrand } from "../utils/checkoutBrand";
import { useBranding } from "../contexts/BrandingContext";
import AutopayBar from "./AutopayBar";
import { logEvent } from "../apis/events";
import { invalidateSubscriptionAccess } from "../hooks/useSubscriptionAccess";
import CreditPurchaseModal from "./CreditPurchaseModal";
import PaymentBreakdown from "./PaymentBreakdown";
import { formatPayable } from "../utils/paymentCharges";
import {
  parsePlansPayload,
  isTrialPlan,
  isMostPopular,
  isCurrentPlan,
  displayPrice,
  paymentAmount,
  savePercent,
  periodSuffix,
  periodUnit,
  checkoutPeriod,
  addonBillingPeriod,
  yearlyOfferLabel,
} from "../utils/planPricing";

/**
 * Mint-themed pricing panel: monthly/yearly toggle, plan cards, optional flow
 * add-ons and AI-credit purchase. Used by the /subscribe page and rendered
 * inline by SubscriptionGate when a page is locked.
 */
const PlansPanel = ({ compact = false, showAddons = true, onSubscribed }) => {
  const brand = useBranding();
  const checkout = resolveCheckoutBrand(brand);
  const [plans, setPlans] = useState([]);
  const [creditPlans, setCreditPlans] = useState([]);
  const [addonPlans, setAddonPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("3_months");
  const [catalog, setCatalog] = useState("v2");
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setIsLoading(true);
        const [plansRes, subRes, creditsRes, addonsRes] = await Promise.allSettled([
          getPlans(),
          getUserSubscription(),
          getCreditPlans(),
          showAddons ? getAddonPlans() : Promise.resolve(null),
        ]);
        if (cancelled) return;

        if (plansRes.status === "fulfilled" && plansRes.value.data.success) {
          const parsed = parsePlansPayload(plansRes.value.data);
          setCatalog(parsed.catalog);
          setHasUsedTrial(parsed.hasUsedTrial);
          setBillingPeriod(parsed.catalog === "legacy" ? "monthly" : "3_months");
          setPlans(parsed.all);
        } else {
          toast.error("Plans are taking too long to load. Please retry.");
        }
        if (subRes.status === "fulfilled" && subRes.value.data.success && subRes.value.data.data) {
          const sub = subRes.value.data.data;
          setCurrentSubscription(new Date(sub.expires_at) > new Date() ? sub : null);
        }
        if (creditsRes.status === "fulfilled" && creditsRes.value.data.success) {
          setCreditPlans(creditsRes.value.data.data || []);
        }
        if (addonsRes.status === "fulfilled" && addonsRes.value?.data?.success) {
          setAddonPlans(addonsRes.value.data.data || []);
        }
      } catch (e) {
        toast.error("Failed to load subscription plans");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    logEvent("plans_viewed", { context: compact ? "gate" : "subscribe_page" });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddonCheckout = ({ order, description, onVerify, abandonMeta }) => {
    openRazorpayCheckout({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency || "INR",
      name: checkout.name,
      image: checkout.image,
      description,
      orderId: order.order_id,
      themeColor: "#16A34A",
      onDismiss: () => {
        logEvent("payment_abandoned", abandonMeta);
        setProcessingPlan(null);
      },
      handler: onVerify,
    });
  };

  const handleSubscribe = async (plan) => {
    setProcessingPlan(plan.plan_name);
    const effectivePeriod = checkoutPeriod(plan, billingPeriod);
    await startPlatformCheckout({
      plan,
      billingPeriod: effectivePeriod,
      checkoutName: checkout.name,
      checkoutImage: checkout.image,
      onSuccess: (subscription) => {
        logEvent("payment_success", { kind: "plan_autopay", plan_name: plan.plan_name, billing_period: effectivePeriod });
        invalidateSubscriptionAccess();
        setCurrentSubscription(subscription || null);
        if (onSubscribed) onSubscribed(subscription);
      },
      onSettled: () => setProcessingPlan(null),
    });
  };

  const handleBuyAddon = async (addon) => {
    setProcessingPlan(`addon_${addon.id}`);
    try {
      await loadScript();
      const addonPeriod = addonBillingPeriod(billingPeriod);
      const orderResponse = await createAddonOrder(addon.plan_key || addon.id, addonPeriod);
      if (!orderResponse.data.success) {
        throw new Error(orderResponse.data.error || "Failed to create add-on order");
      }
      openAddonCheckout({
        order: orderResponse.data.data,
        description: addon.plan_display_name,
        abandonMeta: { kind: "addon", addon_id: addon.id, order_id: orderResponse.data.data.order_id },
        onVerify: async (response) => {
          try {
            const verifyResponse = await verifyAddonPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (!verifyResponse.data.success) {
              throw new Error(verifyResponse.data.error || "Payment verification failed");
            }
            toast.success("Add-on activated!");
            if (onSubscribed) onSubscribed(null);
          } catch (error) {
            toast.error(error.response?.data?.error || error.message || "Payment verification failed");
          } finally {
            setProcessingPlan(null);
          }
        },
      });
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to initiate payment");
      setProcessingPlan(null);
    }
  };

  if (isLoading) {
    return (
      <div className="plans-loading">
        <Loader2 size={22} className="animate-spin" />
      </div>
    );
  }

  if (!plans.length) {
    return (
      <div className="plans-loading" style={{ flexDirection: "column", gap: 10 }}>
        <p style={{ color: "var(--text-2)", fontSize: 14, margin: 0 }}>
          Could not load plans. The server is busy — try again.
        </p>
        <button
          type="button"
          className="ping-btn ping-btn-primary ping-btn-sm"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  const trialPlan = plans.find((p) => isTrialPlan(p));
  const paidPlans = plans.filter((p) => !isTrialPlan(p));
  const isV2 = catalog === "v2";
  const addonPeriod = addonBillingPeriod(billingPeriod);

  const renderPlanCard = (plan, period) => {
    const features =
      typeof plan.features === "string" ? JSON.parse(plan.features || "[]") : plan.features || [];
    const popular = isMostPopular(plan);
    const current = isCurrentPlan(currentSubscription, plan);
    const isProcessing = processingPlan === plan.plan_name;
    const shown = displayPrice(plan, period);
    const due = paymentAmount(plan, period);
    const creditPlan = plan.ai_credit_plan_id
      ? creditPlans.find((cp) => cp.id === plan.ai_credit_plan_id)
      : null;
    const totalAiReplies =
      creditPlan && plan.ai_credits > 0 ? plan.ai_credits * creditPlan.per_credit_ai_replies : 0;
    const trial = isTrialPlan(plan);
    const free = trial && due <= 0;
    const freeDays = parseInt(plan.duration_days, 10) || 3;
    const blockedByOtherPlan = free && currentSubscription && !current;
    const save = !trial && isV2 ? savePercent(plan, period) : 0;

    return (
      <div key={plan.id} className={`plans-card ${popular ? "hi" : ""}`}>
        {free && <span className="plans-badge">Free</span>}
        {popular && <span className="plans-badge">Most popular</span>}
        <h3>{plan.plan_display_name}</h3>
        <div className="plans-price">
          <b>₹{shown.toLocaleString("en-IN")}</b>
          <span>{free ? `for ${freeDays} days` : periodUnit(period)}</span>
        </div>
        {save > 0 && <p className="plans-save">Save {save}%</p>}
        {!isV2 && period === "yearly" && shown !== due && (
          <p className="plans-save">Billed ₹{due.toLocaleString("en-IN")}/year</p>
        )}
        {!free && (
          <PaymentBreakdown
            base={due}
            creditLabel={trial ? "Trial amount" : "Plan amount"}
            compact
          />
        )}
        <ul>
          {features.map((feature, i) => (
            <li key={i}><Check size={13} /> {feature}</li>
          ))}
          <li><Check size={13} /> {plan.max_flows} Flow{plan.max_flows !== 1 ? "s" : ""}</li>
          <li><Check size={13} /> {plan.max_numbers ?? 2} WhatsApp number{(plan.max_numbers ?? 2) !== 1 ? "s" : ""}</li>
          {totalAiReplies > 0 && (
            <li className="ai">
              <Sparkles size={13} /> {plan.ai_credits} AI credits ({totalAiReplies} AI replies)
            </li>
          )}
        </ul>
        <button
          type="button"
          className={`ping-btn ${current ? "ping-btn-ghost" : "ping-btn-green"}`}
          disabled={isProcessing || blockedByOtherPlan || (current && isAutopayOn(currentSubscription))}
          onClick={() => handleSubscribe(plan)}
        >
          {isProcessing ? (
            <Loader2 size={14} className="animate-spin" />
          ) : blockedByOtherPlan ? (
            "Available after your current plan ends"
          ) : current && isAutopayOn(currentSubscription) ? (
            "Current plan · auto-pay on"
          ) : current ? (
            trial
              ? "Current plan"
              : `Renew with auto-pay · ₹${formatPayable(due)}${periodSuffix(period)}`
          ) : free ? (
            "Start free plan"
          ) : trial ? (
            `Start trial · ₹${formatPayable(due)}`
          ) : (
            `Subscribe · ₹${formatPayable(due)}${periodSuffix(period)}`
          )}
        </button>
        <p style={{ fontSize: 12, color: "var(--text-3)", margin: "8px 0 0" }}>
          {free
            ? `Once per account. Active for ${freeDays} days. No payment and no auto-pay.`
            : trial
            ? "One-time payment for 1 month. No auto-pay."
            : `Auto-pay ${isV2 ? (period === "12_months" ? "each year" : period === "6_months" ? "every 6 months" : "every 3 months") : (period === "yearly" ? "each year" : "each month")}. Cancel anytime. Wallet and add-ons stay one-time.`}
        </p>
      </div>
    );
  };

  return (
    <div className={`plans-panel ${compact ? "compact" : ""}`}>
      <AutopayBar
        subscription={currentSubscription}
        onChanged={(sub) => setCurrentSubscription(sub && new Date(sub.expires_at) > new Date() ? sub : null)}
      />
      <div className="plans-toggle">
        {isV2 ? (
          <>
            {[
              { id: "3_months", label: "3 months" },
              { id: "6_months", label: "6 months" },
              { id: "12_months", label: "12 months" },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={billingPeriod === opt.id ? "on" : ""}
                onClick={() => setBillingPeriod(opt.id)}
              >
                {opt.label}
                {opt.id !== "3_months" && paidPlans[0] && savePercent(paidPlans[0], opt.id) > 0 && (
                  <span className="ping-chip g" style={{ marginLeft: 6 }}>
                    Save {savePercent(paidPlans[0], opt.id)}%
                  </span>
                )}
              </button>
            ))}
          </>
        ) : (
          <>
            <button
              type="button"
              className={billingPeriod === "monthly" ? "on" : ""}
              onClick={() => setBillingPeriod("monthly")}
            >
              Monthly
            </button>
            <button
              type="button"
              className={billingPeriod === "yearly" ? "on" : ""}
              onClick={() => setBillingPeriod("yearly")}
            >
              Yearly
            </button>
            {billingPeriod === "yearly" && yearlyOfferLabel(paidPlans[0]) && (
              <span className="ping-chip g">{yearlyOfferLabel(paidPlans[0])}</span>
            )}
          </>
        )}
      </div>

      <div className={`plans-grid ${isV2 || (trialPlan && !hasUsedTrial) ? "cols-4" : ""}`}>
        {trialPlan && !hasUsedTrial && renderPlanCard(trialPlan, isV2 ? "1_month" : "trial")}
        {paidPlans.map((plan) => renderPlanCard(plan, billingPeriod))}
      </div>

      <p style={{ fontSize: 12, color: "var(--text-3)", textAlign: "center", margin: "14px 0 0" }}>
        Prices exclusive of 18% GST. WhatsApp / Meta charges billed separately.
      </p>

      {showAddons && (addonPlans.length > 0 || creditPlans.length > 0) && (
        <div className="plans-addons">
          <p className="plans-addons-title">Add-ons</p>
          <div className="plans-addons-grid">
            {addonPlans.map((addon) => (
              <div key={addon.id} className="plans-addon">
                <div className="ic"><Layers size={15} /></div>
                <div className="grow">
                  <p className="t">{addon.plan_display_name}</p>
                  <p className="s">
                    ₹{(addonPeriod === "yearly" ? addon.yearly_amount : addon.monthly_amount).toLocaleString("en-IN")}
                    {addonPeriod === "yearly" ? "/year" : "/month"}
                    {" · "}pay ₹{formatPayable(addonPeriod === "yearly" ? addon.yearly_amount : addon.monthly_amount)}
                  </p>
                </div>
                <button
                  type="button"
                  className="ping-btn ping-btn-ghost ping-btn-sm"
                  disabled={processingPlan === `addon_${addon.id}`}
                  onClick={() => handleBuyAddon(addon)}
                >
                  {processingPlan === `addon_${addon.id}` ? <Loader2 size={13} className="animate-spin" /> : "Buy"}
                </button>
              </div>
            ))}
            {creditPlans.length > 0 && (
              <div className="plans-addon">
                <div className="ic"><Sparkles size={15} /></div>
                <div className="grow">
                  <p className="t">AI Credits</p>
                  <p className="s">
                    ₹{parseFloat(creditPlans.find((c) => c.is_adding_plan)?.per_credit_price || 1).toLocaleString("en-IN")} per credit ·{" "}
                    {creditPlans.find((c) => c.is_adding_plan)?.per_credit_ai_replies || 10} AI replies each
                  </p>
                </div>
                <button
                  type="button"
                  className="ping-btn ping-btn-ghost ping-btn-sm"
                  onClick={() => setShowCreditModal(true)}
                >
                  Buy credits
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <CreditPurchaseModal
        isOpen={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        onSuccess={() => {
          setShowCreditModal(false);
          if (onSubscribed) onSubscribed(null);
        }}
      />
    </div>
  );
};

export default PlansPanel;
